import { Button, LayoutGrid, Select, SelectProps, TextField, TextFieldProps } from '@archer-technologies/ui-components';
import { IntegratedFiltering } from '@devexpress/dx-react-grid';
import * as React from 'react';
import { isFilterOperationAnEmptyCheck, isFilterValueEmpty } from './helpers';
import { ColumnFilterProps, DefaultPredicateFn, FilterConfig, UseFilterComponentRefs } from './types';

const textColumnIntegratedFilterPredicate: DefaultPredicateFn = (value, filter, row) => {
  switch (filter.operation) {
    case 'equal':
      return String(value).toLowerCase() === String(filter.value).toLowerCase();
    case 'notEqual':
      return String(value).toLowerCase() !== String(filter.value).toLowerCase();
    case 'empty':
      return isFilterValueEmpty(value);
    case 'notEmpty':
      return !isFilterValueEmpty(value);
    default:
      return IntegratedFiltering.defaultPredicate(value, filter, row);
  }
};

const statusColumnFilterPredicate: DefaultPredicateFn = (value, filter, row) => {
  return textColumnIntegratedFilterPredicate(value.statusLabel, filter, row);
};

const useTextColumnFilterRefs: UseFilterComponentRefs = () => {
  const selectHandleClickAwayOverride = React.useRef(false);
  return { handleClickAwayOverrideRefs: { selectHandleClickAwayOverride } };
};

const keyListeners = { enter: 'Enter' };

const TextColumnFilter = ({
  filter,
  columnName,
  columnFilterOperations,
  selectedFilterOperation,
  onFilter,
  addClearFilterStateFn,
  removeClearFilterStateFn,
  messages,
  classNames,
  filterComponentRefs = {}
}: ColumnFilterProps) => {
  const [newFilterValue, setNewFilterValue] = React.useState(filter ? String(filter.value) : '');
  const [newFilterOperation, setNewFilterOperation] = React.useState(selectedFilterOperation);
  const textFieldDisabled = isFilterOperationAnEmptyCheck(newFilterOperation);
  let clearButtonDisabled = isFilterValueEmpty(newFilterValue);
  let applyButtonDisabled = isFilterValueEmpty(newFilterValue) && !isFilterOperationAnEmptyCheck(newFilterOperation);
  if (filter) {
    clearButtonDisabled = isFilterValueEmpty(newFilterValue) && !isFilterOperationAnEmptyCheck(filter.operation);
    applyButtonDisabled = newFilterOperation === selectedFilterOperation && newFilterValue === filter.value;
  }

  const onFilterClear = () => {
    setNewFilterValue('');
    onFilter(null);
    document.body.click();
  };

  const onFilterApply = () => {
    let newFilterConfig: FilterConfig | null = null;
    if (isFilterOperationAnEmptyCheck(newFilterOperation)) {
      setNewFilterValue('');
      newFilterConfig = { value: '', operation: newFilterOperation };
    } else if (!isFilterValueEmpty(newFilterValue)) {
      newFilterConfig = { value: newFilterValue, operation: newFilterOperation };
    }
    onFilter(newFilterConfig);
    document.body.click();
  };

  const setSelectHandleClickAwayOverride = (override: boolean) => {
    const { handleClickAwayOverrideRefs = {} } = filterComponentRefs;
    if (handleClickAwayOverrideRefs.selectHandleClickAwayOverride) {
      const typedOverrideRef = handleClickAwayOverrideRefs.selectHandleClickAwayOverride as React.MutableRefObject<boolean>;
      typedOverrideRef.current = override;
    }
  };

  const operationSelectProps: SelectProps = {
    options: columnFilterOperations.map(operation => ({ name: messages[operation], id: operation })),
    value: newFilterOperation,
    onChange: event => setNewFilterOperation(event.target.value as string),
    onOpen: () => setSelectHandleClickAwayOverride(true),
    onClose: () => setSelectHandleClickAwayOverride(false),
    margin: 'dense',
    inputLabel: messages.columnFilterLabel,
    showLabel: false
  };

  const handleTextFieldKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === keyListeners.enter && !applyButtonDisabled) {
      onFilterApply();
    }
  };

  const textFieldProps: TextFieldProps = {
    value: newFilterValue,
    onChange: event => setNewFilterValue(event.target.value),
    onKeyDown: handleTextFieldKeyDown,
    margin: 'dense',
    label: messages.columnFilterLabel,
    disabled: textFieldDisabled,
    fullWidth: true,
    autoFocus: true
  };

  React.useEffect(() => {
    const clearFilterState = () => setNewFilterValue('');
    addClearFilterStateFn(columnName, clearFilterState);
    return () => removeClearFilterStateFn(columnName);
  }, [addClearFilterStateFn, removeClearFilterStateFn, columnName, setNewFilterValue]);

  return (
    <>
      <LayoutGrid container justify="space-between" className={classNames.mainContainer}>
        <LayoutGrid item xs={4}>
          <Select {...operationSelectProps} />
        </LayoutGrid>
        <LayoutGrid item xs={7}>
          {/* TODO: remove `any` cast */}
          <TextField {...(textFieldProps as any)} />
        </LayoutGrid>
      </LayoutGrid>
      <LayoutGrid container justify="flex-end">
        <LayoutGrid item>
          <Button color="primary" variant="text" size="small" disabled={clearButtonDisabled} onClick={onFilterClear}>
            {messages.clear}
          </Button>
        </LayoutGrid>
        <LayoutGrid item>
          <Button color="primary" size="small" disabled={applyButtonDisabled} onClick={onFilterApply}>
            {messages.apply}
          </Button>
        </LayoutGrid>
      </LayoutGrid>
    </>
  );
};

export { textColumnIntegratedFilterPredicate, statusColumnFilterPredicate, useTextColumnFilterRefs, TextColumnFilter };
